import { useState, type ClipboardEvent, type CSSProperties, type Dispatch, type SetStateAction } from "react";
import { ChevronUp, Send } from "@thuthi/ui/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@thuthi/ui/components/button";
import { Input } from "@thuthi/ui/components/input";
import PublishDialog from "./PublishDialog";
import Navigation from "./Navigation";
import type { Subscription } from "../app/types";

/** 붙여 넣은 이미지 전부(여러 장 가능) */
type PastedImageGetter = (ev: ClipboardEvent<Element>) => File[];

interface MessagingProps {
  selected?: Subscription | null;
  dialogOpenMode: string;
  onDialogOpenModeChange: Dispatch<SetStateAction<string>>;
}

const Messaging = (props: MessagingProps) => {
  const [message, setMessage] = useState("");
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const [dialogKey, setDialogKey] = useState(0);

  const { dialogOpenMode } = props;
  const subscription = props.selected;

  const handleOpenDialogClick = () => {
    props.onDialogOpenModeChange(PublishDialog.OPEN_MODE_DEFAULT);
  };

  const handleDialogClose = () => {
    props.onDialogOpenModeChange("");
    setDialogKey((prev) => prev + 1);
    setAttachFiles([]);
  };

  const getPastedImage: PastedImageGetter = (ev) => {
    const files: File[] = [];
    const { items } = ev.clipboardData;
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
    return files;
  };

  return (
    <>
      {subscription && (
        <MessageBar
          subscription={subscription}
          message={message}
          onMessageChange={setMessage}
          onFilePasted={setAttachFiles}
          onOpenDialogClick={handleOpenDialogClick}
          getPastedImage={getPastedImage}
        />
      )}
      <PublishDialog
        key={`publishDialog${dialogKey}`} // Resets dialog when canceled/closed
        openMode={dialogOpenMode}
        topic={subscription?.topic ?? ""}
        message={message}
        attachFiles={attachFiles}
        getPastedImage={getPastedImage}
        onClose={handleDialogClose}
        onDragEnter={() => props.onDialogOpenModeChange((prev) => prev || PublishDialog.OPEN_MODE_DRAG)} // Only update if not already open
        onResetOpenMode={() => props.onDialogOpenModeChange(PublishDialog.OPEN_MODE_DEFAULT)}
      />
    </>
  );
};

interface MessageBarProps {
  subscription: Subscription;
  message: string;
  onMessageChange: (message: string) => void;
  onFilePasted: (files: File[]) => void;
  onOpenDialogClick: () => void;
  getPastedImage: PastedImageGetter;
}

const MessageBar = (props: MessageBarProps) => {
  const { t } = useTranslation();

  // v2 는 제목이 필수라 바로 보내지 않고, 적은 본문을 담아 보내기 창을 연다(제목을 거기서 적는다)
  const handleSendClick = () => props.onOpenDialogClick();

  const handlePaste = (ev: ClipboardEvent<HTMLInputElement>) => {
    const files = props.getPastedImage(ev);
    if (files.length > 0) {
      props.onFilePasted(files);
      props.onOpenDialogClick();
    }
  };

  return (
    <div
      className="bg-background/95 fixed right-2.5 bottom-2.5 left-2.5 z-40 flex items-center gap-2 rounded-lg border p-2 backdrop-blur-lg sm:right-6 sm:bottom-[18px] sm:left-[var(--message-bar-left)] shadow-[0_18px_45px_rgba(24,33,47,0.14)] dark:shadow-[0_18px_45px_rgba(0,0,0,0.38)]"
      style={{ "--message-bar-left": `${Navigation.width + 24}px` } as CSSProperties}
    >
      <Button
        type="button"
        variant="secondary"
        size="icon"
        onClick={props.onOpenDialogClick}
        aria-label={t("message_bar_show_dialog")}
        className="text-muted-foreground shrink-0 rounded-full"
      >
        <ChevronUp className="size-5" />
      </Button>
      <Input
        autoFocus
        placeholder={t("message_bar_type_message")}
        aria-label={t("message_bar_type_message")}
        role="textbox"
        type="text"
        value={props.message}
        onChange={(ev) => props.onMessageChange(ev.target.value)}
        onKeyDown={(ev) => {
          // isComposing 가드: 원본의 onKeyPress 는 IME 조합 확정 Enter 에 반응하지 않았다. 동일 동작 유지.
          if (ev.key === "Enter" && !ev.nativeEvent.isComposing) {
            ev.preventDefault();
            handleSendClick();
          }
        }}
        onPaste={handlePaste}
        className="bg-background dark:bg-background"
      />
      <Button
        type="button"
        size="icon"
        onClick={handleSendClick}
        aria-label={t("message_bar_publish")}
        className="shrink-0 rounded-full"
      >
        <Send className="size-5" />
      </Button>
    </div>
  );
};

export default Messaging;
